import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Trash2, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const monthLabel = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) : '');
const thisMonth = () => new Date().toISOString().slice(0, 7) + '-01';

// Sheet WO → 365Ops WO rules. Learned when a PM moves a sheet bar to another WO on the Schedule, or added here.
export default function WoRedirects({ canEdit }) {
  const [rules, setRules] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [form, setForm] = useState({ from_wo: '', to_wo: '', effective_from: thisMonth() });
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/manpower/wo-redirects`, auth()).then((r) => setRules(r.data.redirects)).catch(() => setRules([]));
    axios.get(`${API}/manpower/hidden-days`, auth()).then((r) => setHidden(r.data.hidden || [])).catch(() => setHidden([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const restoreHidden = async (h) => {
    if (!window.confirm(`Let the sheet put ${h.sheet_wo} back on the Schedule for ${h.dates.length} day${h.dates.length > 1 ? 's' : ''} (${h.dates[0]} → ${h.dates[h.dates.length - 1]})?`)) return;
    try { await axios.delete(`${API}/manpower/hidden-days/${encodeURIComponent(h.sheet_wo)}`, auth()); toast.success('Those days come back on the next read'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not restore'); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/manpower/wo-redirects`, form, auth());
      toast.success(`Sheet rows with ${form.from_wo.toUpperCase()} now land on ${form.to_wo.toUpperCase()} (from ${monthLabel(form.effective_from)}) — takes effect on the next read`);
      setForm({ from_wo: '', to_wo: '', effective_from: thisMonth() }); setAdding(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add rule'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove the rule ${r.from_wo} → ${r.to_wo} (from ${monthLabel(r.effective_from)})?\n\nSheet rows with ${r.from_wo} will go back to WO ${r.from_wo} on the next read.`)) return;
    try { await axios.delete(`${API}/manpower/wo-redirects/${r.id}`, auth()); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not remove'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm" data-testid="mp-wo-redirects">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-slate-800 font-semibold">Work order redirects</p>
          <p className="text-xs text-slate-500 mt-0.5">When the sheet still says last month's WO (e.g. 26-7040 "August maintenance") but the job now belongs to this month's WO, the read lands it on the right one. Rules are created automatically when you move a sheet bar to another WO on the Schedule.</p>
        </div>
        {canEdit && <button type="button" onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-slate-900" data-testid="mp-redirect-add-toggle"><Plus className="w-3.5 h-3.5" /> Add rule</button>}
      </div>
      {adding && (
        <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2" data-testid="mp-redirect-form">
          <label className="text-xs text-slate-500">Sheet says<input required value={form.from_wo} onChange={(e) => setForm({ ...form, from_wo: e.target.value })} placeholder="26-7040" className="block mt-1 w-28 bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-900 font-mono" data-testid="mp-redirect-from" /></label>
          <ArrowRight className="w-4 h-4 text-slate-500 mb-2" />
          <label className="text-xs text-slate-500">Put it on<input required value={form.to_wo} onChange={(e) => setForm({ ...form, to_wo: e.target.value })} placeholder="26-7047" className="block mt-1 w-28 bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-900 font-mono" data-testid="mp-redirect-to" /></label>
          <label className="text-xs text-slate-500">From month<input type="month" required value={form.effective_from.slice(0, 7)} onChange={(e) => setForm({ ...form, effective_from: e.target.value + '-01' })} className="block mt-1 bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-900" data-testid="mp-redirect-from-month" /></label>
          <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg px-3 py-2" data-testid="mp-redirect-save">Save rule</button>
        </form>
      )}
      <div className="mt-3 space-y-1" data-testid="mp-redirect-list">
        {rules === null && <p className="text-xs text-slate-500">Loading…</p>}
        {rules?.length === 0 && <p className="text-xs text-slate-500">No rules yet.</p>}
        {rules?.map((r) => (
          <div key={r.id} className="flex items-center gap-3 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={`mp-redirect-${r.id}`}>
            <span className="font-mono text-amber-700 font-bold">{r.from_wo}</span>
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
            <span className="font-mono text-emerald-700 font-bold">{r.to_wo}</span>
            <span className="text-slate-500">from {monthLabel(r.effective_from)}{r.customer ? ` · ${r.customer}` : ''}</span>
            <span className="text-slate-500 ml-auto hidden sm:inline">{r.created_by}</span>
            {canEdit && <button type="button" onClick={() => remove(r)} className="text-slate-500 hover:text-red-700" title="Remove rule" data-testid={`mp-redirect-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>
      {hidden.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200" data-testid="mp-hidden-days">
          <p className="text-xs text-slate-500 mb-1.5">Sheet days hidden because the bar was deleted on the Schedule (they won't come back until restored, or until a redirect rule sends them to the right WO):</p>
          <div className="flex flex-wrap gap-2">
            {hidden.map((h) => (
              <span key={h.sheet_wo} className="inline-flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={`mp-hidden-${h.sheet_wo}`}>
                <span className="font-mono text-slate-800 font-bold">{h.sheet_wo}</span>
                <span className="text-slate-500">{h.customer ? `${h.customer} · ` : ''}{h.dates.length} day{h.dates.length > 1 ? 's' : ''}</span>
                {canEdit && <button type="button" onClick={() => restoreHidden(h)} className="text-blue-700 hover:text-slate-900 font-semibold" data-testid={`mp-hidden-restore-${h.sheet_wo}`}>restore</button>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
